import { SOURCES_TYPES } from "@core/constants"
import { Address } from "@types"
import moment from "moment-timezone"
import { capitalizeFirstLetter } from "."

/**
 * Permet de formater un numéro de source selon sa longueur et son type
 * @param idsource - Numéro de la source
 * @param idtype - Type de compteur
 */
export function formatIdSource(idsource: string, idtype: string) {
    if (!idsource) return ""

    // aide: permet d'insérer un text à une position donnée dans un autre texte
    function insertString(str: string, newStr: string, position: number) {
        return str.slice(0, position) + newStr + str.slice(position)
    }

    let newStr = idsource

    if (![SOURCES_TYPES.PDL, SOURCES_TYPES.PCE].includes(idtype)) {
        return idsource
    }

    if (idsource && idsource.length === 14) {
        const format = "### ### ### ### ##"
        for (let i = 0; i < format.length; i++) {
            if (format[i] === " ") newStr = insertString(newStr, " ", i)
        }
    } else if (idsource && idsource.length === 8) {
        const format = "## ######"
        for (let i = 0; i < format.length; i++) {
            if (format[i] === " ") newStr = insertString(newStr, " ", i)
        }
    }
    return newStr
}

/**
 * Convertie une date d'un format ISO à un format selon le format passé en paramètre
 * @param date - Date à formater
 * @param format - Format de la date (ex: "DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", etc.)
 * @param defaultValue - Valeur par défaut si la date est null ou undefined
 * @param timezone - Timezone à utiliser. Par défaut "Europe/Paris"
 * @param locale - Locale pour les noms de mois/jours (ex: "fr", "en"). Par défaut "fr"
 * @returns Date formatée
 */
export const formatDate = (
    date: string | Date | null | undefined,
    format = "DD/MM/YYYY",
    defaultValue = "",
    timezone: string | null = null,
    locale = "fr"
): string => {
    if (!date) return defaultValue
    const tz = timezone ?? "Europe/Paris"
    return moment(date).isValid()
        ? moment(date).tz(tz).locale(locale).format(format)
        : defaultValue
}

/**
 * Formate une date en français via `Intl` (natif). À PRÉFÉRER à
 * `formatDate(..., "fr")` (moment) dès que le format contient des noms de
 * mois/jour : la locale FR de moment ne se bundle pas de façon fiable sur
 * Heroku → les mois retombent en anglais. `Intl` est natif au runtime, sans
 * locale à charger.
 * @param date - Date à formater (ISO string, timestamp ou Date)
 * @param options - Options `Intl.DateTimeFormat` (timezone "Europe/Paris" par
 *   défaut). Défaut : "1 janvier 2026". Ex : `{ dateStyle: "long", timeStyle:
 *   "short" }` → "1 janvier 2026 à 14:30".
 * @param defaultValue - Valeur si la date est absente ou invalide
 * @returns Date formatée en français
 */
export const formatDateFr = (
    date: string | number | Date | null | undefined,
    options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
        year: "numeric",
    },
    defaultValue = ""
): string => {
    if (date === null || date === undefined || date === "") return defaultValue
    const parsed = new Date(date)
    return Number.isNaN(parsed.getTime())
        ? defaultValue
        : new Intl.DateTimeFormat("fr-FR", {
              timeZone: "Europe/Paris",
              ...options,
          }).format(parsed)
}

/**
 * Formate un nombre selon les options passées en paramètre
 * @param number Le nombre à formater
 * @param options Options de formatage
 * @param options.unit Unité à ajouter à la fin du nombre. Par défaut aucune unité
 * @param options.maximumFractionDigits Nombre maximum de chiffres après la virgule. Par défaut 2
 * @param options.style Style de formatage (decimal, percent, currency). Par défaut "decimal"
 * @param options.defaultValue Valeur par défaut si le nombre est NaN. Par défaut "-"
 * @param options.currency Devise (EUR, USD, etc.) si le style est "currency". Par défaut "EUR"
 * @param options.autoCents Si `true` et que `style` est égale à `currency`, alors pour tout `value < 1`, celui-ci est multiplié par 100 et l'unité de le devise est modifiée en "centimes". Par défaut false
 * @returns Le nombre formaté
 *
 * @example
 * formatNumber(0.56, { style: 'currency', currency: 'EUR' }) // "0,56 €"
 * formatNumber(32, { style: 'currency', currency: 'EUR', unit: 'kWh' }) // "32€/kWh"
 * formatNumber(0.0056, { style: 'currency', currency: 'EUR', autoCents: true, unit: 'kWh' }) // "0,56 centimes/kWh"
 * formatNumber(null, { defaultValue: 'N/A' }) // "N/A"
 */
export function formatNumber(
    value?: number | null,
    options: {
        unit?: string
        minimumFractionDigits?: number
        maximumFractionDigits?: number
        style?: "decimal" | "percent" | "currency"
        defaultValue?: string
        currency?: string
        autoCents?: boolean
    } = {
        unit: "",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        style: "decimal",
        defaultValue: "-",
        currency: "EUR",
        autoCents: false,
    }
) {
    const {
        unit = "",
        minimumFractionDigits = 0,
        maximumFractionDigits = 2,
        style = "decimal",
        defaultValue = "-",
        currency = "EUR",
        autoCents = false,
    } = options

    if (value === undefined || value === null) {
        return defaultValue ?? ""
    }

    if (isNaN(value) || value === null) return defaultValue ?? ""

    // si on est en décimal et que la valeur est inférieure à 1, on convertit le prix en centimes et on le renvoie au format "X centimes{/unit}".
    if (autoCents && style === "currency" && value < 1) {
        const centValue = value * 100
        return formatNumber(centValue, {
            unit: unit ? `centimes/${unit}` : "centimes",
            minimumFractionDigits,
            maximumFractionDigits,
            defaultValue,
            style: "decimal",
            autoCents: false, // pour éviter une boucle infinie
        })
    }

    const formattedValue = Intl.NumberFormat("fr-FR", {
        style: style,
        minimumFractionDigits: minimumFractionDigits,
        maximumFractionDigits: maximumFractionDigits,
        currency: currency,
    }).format(value)

    if (unit) {
        // Si on est en style currency, on ajoute l'unité après une /
        if (style === "currency") {
            return `${formattedValue}/${unit}`
        }
        return `${formattedValue} ${unit}`
    }
    return formattedValue
}

/**
 * Formate une adresse
 * @param address - Adresse à formater
 * @returns Adresse formétée
 */
export const formatAddress = (
    address:
        | Omit<Address, "citycode" | "inline_name" | "country">
        | null
        | undefined
) => {
    if (address) {
        return `${address.street ? address.street + "," : ""} ${
            address.zipcode ? address.zipcode : ""
        } ${address.city ? address.city : ""}`
    }
    return ""
}

/**
 * Formate un nom de personne en capitalisant la première lettre du prénom et du nom
 * @param firstname - Prénom de la personne
 * @param lastname - Nom de la personne
 * @returns Nom complet formaté
 */
export function formatPerson(firstname: string, lastname: string) {
    return `${capitalizeFirstLetter(firstname)} ${capitalizeFirstLetter(
        lastname
    )}`.trim()
}

/**
 * Fonction permettant de formater l'évolution au format attendu
 * @example
 * ```ts
 * formatEvolution(0.1, "temperature_air_interieur")
 * // "10% (1°C)"
 * ```
 * @param evolution - Valeur de l'évolution
 * @parm diff - Différence entre les deux valeurs
 * @param maxDigit - Nombre de chiffres après la virgule
 * @param unit - Unité de la variable
 */
export function formatEvolution(
    evolution: number,
    diff: number,
    {
        maxDigit,
        unit,
    }: {
        maxDigit: number
        unit: string
    } = {
        maxDigit: 1,
        unit: "",
    }
) {
    const evolutionFormated = formatNumber(evolution, {
        style: "percent",
        maximumFractionDigits: maxDigit,
    })

    return `${evolution > 0 ? "+" : ""}${evolutionFormated ?? ""} (${formatNumber(
        diff,
        {
            unit: unit,
            maximumFractionDigits: maxDigit,
        }
    )})`
}

/**
 * Formate un numéro de téléphone au format français
 * @param str - Chaîne à formater
 * @returns Numéro formaté (ex: "06 12 34 56 78" ou "+33 6 12 34 56 78")
 */
export function formatPhone(str: string | null | undefined): string {
    if (!str) return ""

    let digits = str.replace(/\D/g, "")

    if (digits.startsWith("0033")) digits = digits.slice(2)

    if (digits.startsWith("33")) {
        if (/^33\d{9}$/.test(digits)) {
            return digits.replace(
                /^33(\d)(\d{2})(\d{2})(\d{2})(\d{2})$/,
                "+33 $1 $2 $3 $4 $5"
            )
        }
        return digits
    }
    if (/^0\d{9}$/.test(digits)) {
        return digits.replace(
            /^(0\d)(\d{2})(\d{2})(\d{2})(\d{2})$/,
            "$1 $2 $3 $4 $5"
        )
    }
    return digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim()
}
